import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DashboardConfig, TownConfig } from '../types/config';

const STORAGE_KEY = 'dashboard_configs';

export function useDashboardConfig() {
  const [configs, setConfigs] = useState<DashboardConfig[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  }, [configs]);

  const saveConfig = (name: string, config: TownConfig) => {
    const newConfig: DashboardConfig = {
      id: uuidv4(),
      name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      config
    };

    setConfigs(current => [...current, newConfig]);
    return newConfig.id;
  };

  const updateConfig = (id: string, config: Partial<TownConfig>) => {
    setConfigs(current => current.map(c => {
      if (c.id === id) {
        return {
          ...c,
          updatedAt: new Date().toISOString(),
          config: { ...c.config, ...config }
        };
      }
      return c;
    }));
  };

  const getConfig = (id: string) => {
    return configs.find(c => c.id === id);
  };

  const deleteConfig = (id: string) => {
    setConfigs(current => current.filter(c => c.id !== id));
  };

  const listConfigs = () => {
    return configs.map(({ id, name, createdAt, updatedAt }) => ({
      id,
      name,
      createdAt,
      updatedAt
    }));
  };

  const importConfigFromJson = (json: Record<string, unknown>, nameOverride?: string) => {
    let config: TownConfig;

    // Check if it's the old format { config: ... } or the new raw format
    if (json && typeof json === 'object' && 'config' in json) {
      config = json.config as TownConfig;
    } else if (json && typeof json === 'object' && 'name' in json && 'data' in json) {
      // Assume it's a raw TownConfig
      config = json as unknown as TownConfig;
    } else {
      throw new Error('Invalid JSON structure: missing config property or valid TownConfig structure');
    }

    const dashboardName = nameOverride || (typeof config.name === 'string' ? config.name : 'Imported Dashboard');
    const newConfig: DashboardConfig = {
      id: uuidv4(),
      name: dashboardName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      config: config
    };
    setConfigs(current => [...current, newConfig]);
    return newConfig.id;
  };

  return {
    saveConfig,
    importConfigFromJson,
    updateConfig,
    getConfig,
    deleteConfig,
    listConfigs
  };
}